import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import artistMain from '../assets/artist-main.png';
import artistTreasure from '../assets/artist-treasure.png';
import albumCover from '../assets/album-cover.png';
import irdt2bCover from '../assets/IRDT2B.png';
import wthnViewPic from '../assets/WTHNViewEpkPic.png';
import thimPic from '../assets/THIMepkPic.png';
import igbr2i2dCover from '../assets/IGBR2I2Dalbumcover.png';
import hfPic from '../assets/HFepkPic.png';
import ftswPic from '../assets/FTSWepkPic.png';
import itmShowPoster from '../assets/ITMShowPoster2.png';
import docuMEANTaryPic from '../assets/docuMEANTary2.png';
import bizModelsPic from '../assets/BizModelsEpkPic.png';
import c2RepPic from '../assets/C2RepkPic.png';
import tmogfBookPic from '../assets/TMOGFbookEpkPic.png';
import irdt2bBookPic from '../assets/IRDT2BbookEpkPic.png';
import musicBookPic from '../assets/MusicBookBplan.png';
import s365BookPic from '../assets/365SbookEpkPic.png';
import gfAppThumb from '../assets/GFappThumbnail.png';
import geLogoThumb from '../assets/GElogo2Thumb.png';

import { generatePressKit } from '../lib/pressKit';

type Section = 'home' | 'bio' | 'music' | 'media' | 'game' | 'contact';

const navItems: { id: Section; label: string; icon: string }[] = [
  { id: 'home', label: 'Home', icon: '🏠' },
  { id: 'bio', label: 'Bio', icon: '📖' },
  { id: 'music', label: 'Music', icon: '🎵' },
  { id: 'media', label: 'Media', icon: '🎬' },
  { id: 'game', label: 'Game', icon: '🎲' },
  { id: 'contact', label: 'Contact', icon: '📩' },
];

const EPKPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<Section>('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleNav = (section: Section) => {
    if (section === 'game') {
      navigate('/game');
      return;
    }
    setActiveSection(section);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">👑</span>
            <span className="font-black text-sm tracking-wider text-cyan-400">THE MACK REP</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden fixed top-14 left-0 right-0 z-40 bg-black/95 backdrop-blur-md border-b border-white/10"
          >
            <div className="p-4 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    activeSection === item.id
                      ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span className="font-bold text-sm tracking-wider uppercase">{item.label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-72 bg-gradient-to-b from-black via-gray-950 to-black border-r border-white/10 z-40 flex-col">
          {/* Logo Area */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="text-3xl">👑</div>
              <div>
                <h1 className="font-black text-lg tracking-wider text-cyan-400">THE MACK REP</h1>
                <p className="text-[10px] tracking-[0.3em] text-gray-500 uppercase">Electronic Press Kit</p>
              </div>
            </div>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all duration-300 group ${
                  activeSection === item.id
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border border-cyan-500/30 shadow-lg shadow-cyan-500/10'
                    : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
              >
                <span className="text-xl group-hover:scale-110 transition-transform">{item.icon}</span>
                <span className="font-bold text-sm tracking-wider uppercase">{item.label}</span>
                {activeSection === item.id && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="ml-auto w-1.5 h-8 bg-cyan-400 rounded-full"
                  />
                )}
              </button>
            ))}
          </nav>

          {/* Bottom Artist Image */}
          <div className="p-4 border-t border-white/10">
            <div className="relative rounded-xl overflow-hidden">
              <img src={artistTreasure} alt="Kiley Kelly" className="w-full h-40 object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <p className="absolute bottom-3 left-3 text-xs font-bold text-cyan-400 tracking-wider">KILEY KELLY</p>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-72 min-h-screen pt-16 lg:pt-0">
          <AnimatePresence mode="wait">
            {activeSection === 'home' && <HomeSection key="home" onSeeMore={() => handleNav('bio')} />}
            {activeSection === 'bio' && <BioSection key="bio" />}
            {activeSection === 'music' && <MusicSection key="music" />}
            {activeSection === 'media' && <MediaSection key="media" />}
            {activeSection === 'contact' && <ContactSection key="contact" />}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

/* ======================== HOME SECTION ======================== */
const HomeSection: React.FC<{ onSeeMore: () => void }> = ({ onSeeMore }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen"
    >
      {/* Hero */}
      <div className="relative h-[70vh] lg:h-[85vh] overflow-hidden">
        <img src={artistMain} alt="Kiley Kelly" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60" />

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="text-4xl">👑</div>
              <div className="h-px flex-1 bg-gradient-to-r from-cyan-400 to-transparent" />
            </div>
            <h1 className="text-5xl lg:text-7xl font-black mb-2 leading-none">
              <span className="text-white">KILEY</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">KELLY</span>
            </h1>
            <p className="text-xl lg:text-2xl font-bold text-amber-400 tracking-wider mt-2">THE MACK REP</p>
          </motion.div>
        </div>
      </div>

      {/* Bio Preview */}
      <div className="px-6 lg:px-12 py-12 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-cyan-500/50" />
            <span className="text-cyan-400 font-bold text-sm tracking-[0.3em] uppercase">About</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-cyan-500/50" />
          </div>

          <p className="text-lg lg:text-xl text-gray-300 leading-relaxed font-light">
            Kiley Kelly, <span className="text-cyan-400 font-bold">The Mack Rep</span> was born and raised in South Sacramento, Meadowview California. At a early age he discovered his passion for music but his rap career was put on hold by a <span className="text-amber-400 font-semibold">18 year fight with the system</span>…
          </p>

          <button
            onClick={onSeeMore}
            className="mt-8 group relative inline-flex items-center gap-3 px-8 py-4 rounded-xl font-bold text-sm tracking-wider uppercase overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-purple-500 transition-all duration-300 group-hover:from-cyan-400 group-hover:to-purple-400" />
            <span className="relative text-black">See More</span>
            <svg className="relative w-4 h-4 text-black group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </motion.div>

        {/* Social Media */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-16"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-purple-500/50" />
            <span className="text-purple-400 font-bold text-sm tracking-[0.3em] uppercase">Connect</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-purple-500/50" />
          </div>

          <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
            {/* Facebook */}
            <a href="https://www.facebook.com/profile.php?id=61566553197897&mibextid=ZbWKwL" target="_blank" rel="noopener noreferrer" className="group relative w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-blue-500/50 hover:bg-blue-500/10 transition-all duration-300">
              <svg className="w-8 h-8 lg:w-10 lg:h-10 text-gray-400 group-hover:text-blue-500 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>

            {/* Instagram */}
            <a href="https://www.instagram.com/themackrep?igsi=aDcwOGQwcDYwd3Jl" target="_blank" rel="noopener noreferrer" className="group relative w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-pink-500/50 hover:bg-pink-500/10 transition-all duration-300">
              <svg className="w-8 h-8 lg:w-10 lg:h-10 text-gray-400 group-hover:text-pink-500 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>

            {/* TikTok */}
            <a href="https://www.tiktok.com/@go.flicks?_r=1&_t=ZT-99THqzu6d5C" target="_blank" rel="noopener noreferrer" className="group relative w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-cyan-400/50 hover:bg-cyan-400/10 transition-all duration-300">
              <svg className="w-8 h-8 lg:w-10 lg:h-10 text-gray-400 group-hover:text-cyan-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46v-7.13a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-.81-.07 4.8 4.8 0 01.63-4.42z"/>
              </svg>
            </a>

            {/* YouTube */}
            <a href="https://youtube.com/@goflicks4991?si=OdIupJvparZQNHu2" target="_blank" rel="noopener noreferrer" className="group relative w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-red-500/50 hover:bg-red-500/10 transition-all duration-300">
              <svg className="w-8 h-8 lg:w-10 lg:h-10 text-gray-400 group-hover:text-red-500 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

/* ======================== BIO SECTION ======================== */
const BioSection: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen px-6 lg:px-12 py-12"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-4xl">📖</span>
            <div className="h-px flex-1 bg-gradient-to-r from-cyan-500 to-transparent" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-black">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">BIOGRAPHY</span>
          </h2>
        </div>

        {/* Hero Image */}
        <div className="relative rounded-2xl overflow-hidden mb-12 group">
          <img src={artistMain} alt="Kiley Kelly" className="w-full h-64 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <p className="text-cyan-400 font-bold tracking-wider text-sm">THE MACK REP</p>
            <p className="text-white text-2xl font-black">KILEY KELLY</p>
          </div>
        </div>

        {/* Bio Content */}
        <div className="space-y-8 text-gray-300 leading-relaxed text-lg">
          <div className="relative pl-6 border-l-2 border-cyan-500/50">
            <p>
              Kiley Kelly, <span className="text-cyan-400 font-bold">The Mack Rep</span> was born and raised in South Sacramento, Meadowview California. At a early age he discovered his passion for music but his rap career was put on hold by a <span className="text-amber-400 font-semibold">18 year fight with the system</span>.
            </p>
          </div>

          <div className="relative pl-6 border-l-2 border-purple-500/50">
            <p>
              Growing up in one of Sacramento's most challenging neighborhoods, Kiley found his voice through music. The streets of Meadowview became his classroom, and the rhythm of hip-hop became his curriculum. His natural talent for storytelling and wordplay set him apart from his peers, even before he fully recognized his own potential.
            </p>
          </div>

          <div className="relative pl-6 border-l-2 border-amber-500/50">
            <p>
              But destiny had other plans. An 18-year battle with the criminal justice system would test every fiber of his being. Behind bars, where many lose themselves, Kiley found his voice. He began writing music prolifically — channeling pain, experience, and raw emotion into lyrics that would eventually become his signature sound.
            </p>
          </div>

          <div className="relative pl-6 border-l-2 border-cyan-500/50">
            <p>
              Now a free man with a purpose, Kiley Kelly is on a mission. As <span className="text-cyan-400 font-bold">The Mack Rep</span>, he brings an authenticity to hip-hop that can't be manufactured. His music tells the real stories of survival, redemption, and the relentless pursuit of dreams against all odds.
            </p>
          </div>

          <div className="relative pl-6 border-l-2 border-purple-500/50">
            <p>
              From the concrete jungle of Meadowview to the studio, Kiley's journey is nothing short of extraordinary. His debut project showcases a mature artist who has lived every word he raps — a rare quality in today's music landscape. With a growing fanbase and undeniable talent, The Mack Rep is poised to make his mark on the industry.
            </p>
          </div>

          {/* Quote Block */}
          <div className="my-12 p-8 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 rounded-2xl border border-white/10 text-center">
            <div className="text-4xl mb-4">👑</div>
            <p className="text-2xl lg:text-3xl font-black italic text-white mb-2">
              "From the cell to the mic, every bar is real life"
            </p>
            <p className="text-cyan-400 font-bold tracking-wider">— THE MACK REP</p>
          </div>

          {/* Second Image */}
          <div className="relative rounded-2xl overflow-hidden group">
            <img src={artistTreasure} alt="Kiley Kelly" className="w-full h-64 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ======================== MUSIC SECTION ======================== */
const MusicSection: React.FC = () => {
  const [playingTrack, setPlayingTrack] = useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const handlePlay = (trackId: string, src: string) => {
    if (playingTrack === trackId) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlayingTrack(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(src);
    audioRef.current = audio;
    audio.play();
    setPlayingTrack(trackId);
    audio.onended = () => setPlayingTrack(null);
  };

  const mainHits = [
    {
      id: 'the-hoods-in-me',
      title: "The Hood's In Me",
      number: '01',
      art: irdt2bCover,
      src: '/HighestInTheView.mp3',
    },
    {
      id: 'highest-in-the-view',
      title: 'We The Highest In The View',
      number: '02',
      art: thimPic,
      src: '/TheHoodsInMe2.mp3',
    },
  ];

  const secondMainHits = [
    {
      id: 'hood-famous',
      title: 'Hood Famous',
      number: '01',
      art: hfPic,
      src: '/Hood Famous (1).mp3',
    },
    {
      id: 'in-a-hood-near-you',
      title: 'In A Hood Near You',
      number: '02',
      art: ftswPic,
      src: '/In A Hood Near You.mp3',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen px-6 lg:px-12 py-12"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-4xl">🎵</span>
            <div className="h-px flex-1 bg-gradient-to-r from-cyan-500 to-transparent" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-black">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">MUSIC</span>
          </h2>
        </div>

        {/* Album Feature */}
        <div className="relative rounded-2xl overflow-hidden mb-8 bg-gradient-to-br from-gray-900 to-black border border-white/10">
          <div className="flex flex-col md:flex-row lg:flex-row">
            <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 lg:w-60 lg:h-60 relative flex-shrink-0 p-4 flex items-center justify-center">
              <img src={wthnViewPic} alt="Album Cover" className="w-full h-full object-contain rounded-xl" />
            </div>
            <div className="p-8 flex-1">
              <p className="text-cyan-400 font-bold text-sm tracking-[0.3em] mb-2">DEBUT ALBUM</p>
              <h3 className="text-3xl font-black text-white mb-2">I'LL RATHER DIE THAN BE BROKE</h3>
              <p className="text-gray-400 mb-6">The debut project from The Mack Rep — raw, unfiltered, and authentic.</p>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <span>15 Tracks</span>
                <span>•</span>
                <span>45 minutes</span>
                <span>•</span>
                <span>2026</span>
              </div>
              <a
                href="https://music.youtube.com/playlist?list=PLWwPCAxbWAlI&si=1Et4bxOem8bOGcLP"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-500 rounded-full text-white font-bold text-sm hover:from-red-500 hover:to-red-400 transition-all shadow-lg shadow-red-500/20"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Listen To Whole Album Here
              </a>
            </div>
          </div>
        </div>

        {/* Main Hits */}
        <div className="mt-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-cyan-500/50 to-transparent" />
            <h3 className="text-2xl font-black text-white">Main Hits</h3>
            <div className="h-px flex-1 bg-gradient-to-l from-cyan-500/50 to-transparent" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 sm:gap-6 max-w-md sm:max-w-lg mx-auto">
            {mainHits.map((track, i) => (
              <motion.div
                key={track.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => handlePlay(track.id, track.src)}
                className={`rounded-2xl overflow-hidden transition-all cursor-pointer border ${
                  playingTrack === track.id
                    ? 'bg-cyan-500/10 border-cyan-500/30'
                    : 'hover:bg-white/5 border-white/10 hover:border-white/20'
                }`}
              >
                <img src={track.art} alt={track.title} className="w-full aspect-[5/8] object-cover" />
                <div className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4">
                  <span className="text-gray-600 font-mono text-xs sm:text-sm w-6 sm:w-8">{track.number}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-xs sm:text-sm transition-colors truncate ${playingTrack === track.id ? 'text-cyan-400' : 'text-white'}`}>
                      {track.title}
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    {playingTrack === track.id ? (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Second Album Feature */}
        <div className="relative rounded-2xl overflow-hidden mb-8 bg-gradient-to-br from-gray-900 to-black border border-white/10">
          <div className="flex flex-col md:flex-row lg:flex-row">
            <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 lg:w-60 lg:h-60 relative flex-shrink-0 p-4 flex items-center justify-center">
              <img src={igbr2i2dCover} alt="Album Cover" className="w-full h-full object-contain rounded-xl" />
            </div>
            <div className="p-8 flex-1">
              <p className="text-cyan-400 font-bold text-sm tracking-[0.3em] mb-2">SECOND INDEPENDENT LP</p>
              <h3 className="text-3xl font-black text-white mb-2">I'M GON'T BE RICH IF I DON'T DIE</h3>
              <p className="text-gray-400 mb-6">Second Independent LP from The Mack Rep – game tight, gritty and unapologetically real.</p>
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <span>15 Tracks</span>
                <span>•</span>
                <span>54 minutes</span>
                <span>•</span>
                <span>2026</span>
              </div>
              <a
                href="https://youtube.com/@goflicks4991?si=OdIupJvparZQNHu2"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-500 rounded-full text-white font-bold text-sm hover:from-red-500 hover:to-red-400 transition-all shadow-lg shadow-red-500/20"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Listen To Whole Album Here
              </a>
            </div>
          </div>
        </div>

        {/* Second Main Hits */}
        <div className="mt-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-gradient-to-r from-cyan-500/50 to-transparent" />
            <h3 className="text-2xl font-black text-white">Main Hits</h3>
            <div className="h-px flex-1 bg-gradient-to-l from-cyan-500/50 to-transparent" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 sm:gap-6 max-w-md sm:max-w-lg mx-auto">
            {secondMainHits.map((track, i) => (
              <motion.div
                key={track.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => handlePlay(track.id, track.src)}
                className={`rounded-2xl overflow-hidden transition-all cursor-pointer border ${
                  playingTrack === track.id
                    ? 'bg-cyan-500/10 border-cyan-500/30'
                    : 'hover:bg-white/5 border-white/10 hover:border-white/20'
                }`}
              >
                <img src={track.art} alt={track.title} className="w-full aspect-[5/8] object-cover" />
                <div className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4">
                  <span className="text-gray-600 font-mono text-xs sm:text-sm w-6 sm:w-8">{track.number}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-xs sm:text-sm transition-colors truncate ${playingTrack === track.id ? 'text-cyan-400' : 'text-white'}`}>
                      {track.title}
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    {playingTrack === track.id ? (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Streaming Links */}
        <div className="mt-12 p-6 bg-white/5 rounded-2xl border border-white/10">
          <p className="text-center text-gray-400 text-sm mb-4">Available on all platforms</p>
          <div className="flex justify-center gap-6">
            <span className="text-gray-500 font-bold text-sm">Spotify</span>
            <span className="text-gray-500 font-bold text-sm">YouTube Music</span>
            <span className="text-gray-500 font-bold text-sm">SoundCloud</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ======================== MEDIA SECTION ======================== */
const MediaSection: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen px-6 lg:px-12 py-12"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-4xl">🎬</span>
            <div className="h-px flex-1 bg-gradient-to-r from-cyan-500 to-transparent" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-black">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">MEDIA</span>
          </h2>
          <p className="text-gray-400 mt-3">Films, books, and more</p>
        </div>

        {/* Films Title */}
        <div className="mb-6">
          <h3 className="text-2xl font-black text-white">Films</h3>
        </div>

        {/* Film Card 1 - In The Making */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300 mb-6"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Description Left */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">DocuSeries</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">In The Making</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                A behind the scenes documentary following Kiley Kelly's journey from incarceration to the inception of Go Flicks, to the record industry and beyond! This film is meant to be paired with the tap technology book called, "The Making Of Go Flicks" (below) where after the end of every chapter you can tap in to watch the episode that corresponds to that chapter.
              </p>
            </div>
            {/* Image Right */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={docuMEANTaryPic}
                alt="In The Making Show Poster"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
          </div>
        </motion.div>

        {/* Film Card 2 - docuMEANTary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Description Left */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">DocuSeries</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">docuMEANTary</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Part two of the docuseries In The Making, that shows the journey of Go Flicks, from startup to household name, to Goglomerate and beyond, all while saving and changing lives for the better through a non-profit named Injustice League.
              </p>
            </div>
            {/* Image Right */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={itmShowPoster}
                alt="docuMEANTary Poster"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
          </div>
        </motion.div>

        {/* Film Card 3 - Business Models Of Go Flicks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300 mb-6"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Description Left */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Reality TV</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">Business Models Of Go Flicks</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                The business models of Go Flicks follows 5 beautiful female employees of Go Flicks on their journey of everyday life, on and off the job. Including a side hustle of pursuing a modeling career. In this epic reality show you get brains and beauty that will have you on the edge of your seat!
              </p>
            </div>
            {/* Image Right */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={bizModelsPic}
                alt="Business Models Of Go Flicks"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
          </div>
        </motion.div>

        {/* Film Card 4 - Go Flicks | Cut To Reality */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Description Left */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Reality TV</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">Go Flicks | Cut To Reality</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Follow top Go Flicks influencers as they influence the world through social media posts on Go Flicks. We film them as they leave their mark on the world as seen through posts left behind all over the globe.
              </p>
            </div>
            {/* Image Right */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={c2RepPic}
                alt="Go Flicks Cut To Reality"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
          </div>
        </motion.div>

        {/* Books Title */}
        <div className="mt-12 mb-6">
          <h3 className="text-2xl font-black text-white">Books</h3>
        </div>

        {/* Book Card 1 - The Making Of Go Flicks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300 mb-6"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Image Left */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={tmogfBookPic}
                alt="The Making Of Go Flicks Book Cover"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
            {/* Description Right */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Non-Fiction/Autobiography</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">The Making Of Go Flicks | Movie Book</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                This book is the gritty true story about the origins of Go Flicks. It takes you through the ups and downs of starting the Go Flicks brand where you get a glimpse into the mastermind behind the brands struggle to bring forth the social media app taking the world by storm. This book is equipped with tap technology where you can tap your smartphones in certain places and be transported to additional content (including episodes in the ITM docuseries which this book is meant to be paired with). This movie book is the first of its kind and is a must read for anyone into social media or anyone starting a business.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Book Card 2 - I'll Rather Die Than Be Booked */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Image Left */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={irdt2bBookPic}
                alt="I'll Rather Die Than Be Booked Book Cover"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
            {/* Description Right */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Non-Fiction</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">I'll Rather Die Than Be Booked | Music Book</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                In this work of art the author Kiley Kelly breaks down The Mack Reps lyrics bar for bar for his debut album, 'I'll Rather Die Than Be Broke'. It's a wild ride through the mind of the gifted artist taking the world to new heights. This book is also a tap technology book that takes readers to more content at the tap of a phone (including to streaming of each song). Read along then tell me if the backstory doesn't change your perspective on the lyrics and songs.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Book Card 3 - I'm Gon't Be Rich If I Don't Die | Music Book */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300 mb-6"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Image Left */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={musicBookPic}
                alt="I'm Gon't Be Rich If I Don't Die Music Book Cover"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
            {/* Description Right */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Non-Fiction</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">I'm Gon't Be Rich If I Don't Die | Music Book</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                This book is the sequel to the 'I'll Rather Die Than Be Booked' music book. Once again Kiley Kelly breaks down the lyrics of his rap persona 'The Mack Reps' second LP album, 'I'm Gon't Be Rich If I Don't Die'. Kiley masterfully delivers the backstory of each song bar by bar. Beware, some of this might shock you as he takes you through thoughts that never entered your brain. With no filter Kiley dives deep into his brain child and reveals some double entendre's hidden in plain earshot. This book also has tap technology, so keep your smartphones nearby to see some juicy additional content.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Book Card 4 - 365Startup | Business Devotional */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Image Left */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={s365BookPic}
                alt="365Startup Business Devotional Book Cover"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
            {/* Description Right */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Devotional</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">365Startup | Business Devotional</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Do you have a business idea that you have been toying with in your mind but you have no clue where to start. Well this book is meant for you! Everyday for a year we show you the general business knowledge that you will need to jump start your startup. Along with knowledge, the devotional provides motivation for when things get tough. We walk you through the process of building a business from scratch step by step, day by day and page by page. By the end of the year you will be fully coached and ready to launch your business. This is also a tap technology book full of additional content that will help you on your journey.
              </p>
            </div>
          </div>
        </motion.div>

        {/* More Title */}
        <div className="mt-12 mb-6">
          <h3 className="text-2xl font-black text-white">More</h3>
        </div>

        {/* Service Card 1 - Go Flicks App */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300 mb-6"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Description Left */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Social Media</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">Go Flicks App</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                By now you are probably wondering what exactly is Go Flicks, since we have all this content for it (as seen above). Go Flicks is a on the go social media app that uses AR and geo-location to revolutionize the industry. In other words members can post videos to any geographical location and give those who follow in their footsteps something to watch. Through this service you can get real-time feedback about your surroundings and socialize with nearby members in a all new way! This app is jam-packed with exciting new features. From Sky Stories, to Geo-Messages and much more! Not only is this app fun it can also be profitable through receiving donations, crowd advertising, crowd selling and more!
              </p>
              <a
                href="https://youtu.be/8nn77Y44ZRQ"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-xl font-bold text-sm text-black tracking-wider uppercase hover:from-cyan-400 hover:to-purple-400 transition-all duration-300 w-fit"
              >
                Watch Video
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </a>
            </div>
            {/* Image Right */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={geLogoThumb}
                alt="Go Flicks App Thumbnail"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
          </div>
        </motion.div>

        {/* Service Card 2 - Go Eternal App */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
          className="group p-6 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all duration-300"
        >
          <div className="flex flex-col md:flex-row gap-6">
            {/* Description Left */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">Social Media/Artificial Intelligence</span>
                <span className="text-[10px] text-gray-500">Coming Soon</span>
              </div>
              <h3 className="text-xl font-black text-white mb-3">Go Eternal App</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Go Eternal is a legacy app that gets to know the members in and out through questions, social media posts, books, music and film content that you consume and more. Once the AI knows you well enough it can then answer questions for you that other members want to know about and from you. Although your AI profile it is meant to be communicated with once you are deceased you can set your AI profile to public and anyone can get to know you while living. This is a great way to get to know your favorite celebrities, politicians, intellectuals or even staying in touch with your military personnel loved ones overseas. You have full control of your information. You can make your entries be for only family, only friends, family and friends or the public. Honestly, there are many other features as well that will make your Go Eternal experience enjoyable for all eternity!
              </p>
            </div>
            {/* Image Right */}
            <div className="w-full md:w-80 flex-shrink-0">
              <img
                src={gfAppThumb}
                alt="Go Eternal App Logo"
                className="w-full h-auto rounded-xl object-contain"
              />
            </div>
          </div>
        </motion.div>

      </div>
    </motion.div>
  );
};

/* ======================== CONTACT SECTION ======================== */
const ContactSection: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen px-6 lg:px-12 py-12"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-4xl">📩</span>
            <div className="h-px flex-1 bg-gradient-to-r from-cyan-500 to-transparent" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-black">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">CONTACT</span>
          </h2>
          <p className="text-gray-400 mt-3">Bookings, features, and collaborations</p>
        </div>

        {/* Contact Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Booking */}
          <div className="p-8 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded-2xl border border-white/10">
            <div className="text-3xl mb-4">🎤</div>
            <h3 className="text-xl font-black text-white mb-2">BOOKING</h3>
            <p className="text-gray-400 text-sm mb-4">For live performances, appearances, and events</p>
            <a href="mailto:booking@themackrep.com" className="text-cyan-400 font-bold text-sm hover:text-cyan-300 transition-colors">
              booking@themackrep.com
            </a>
          </div>

          {/* Management */}
          <div className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl border border-white/10">
            <div className="text-3xl mb-4">💼</div>
            <h3 className="text-xl font-black text-white mb-2">MANAGEMENT</h3>
            <p className="text-gray-400 text-sm mb-4">For business inquiries and partnerships</p>
            <a href="mailto:management@themackrep.com" className="text-purple-400 font-bold text-sm hover:text-purple-300 transition-colors">
              management@themackrep.com
            </a>
          </div>

          {/* Press */}
          <div className="p-8 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl border border-white/10">
            <div className="text-3xl mb-4">📰</div>
            <h3 className="text-xl font-black text-white mb-2">PRESS & MEDIA</h3>
            <p className="text-gray-400 text-sm mb-4">Interview requests and press inquiries</p>
            <a href="mailto:press@themackrep.com" className="text-amber-400 font-bold text-sm hover:text-amber-300 transition-colors">
              press@themackrep.com
            </a>
          </div>

          {/* Fan Mail */}
          <div className="p-8 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl border border-white/10">
            <div className="text-3xl mb-4">✉️</div>
            <h3 className="text-xl font-black text-white mb-2">FAN MAIL</h3>
            <p className="text-gray-400 text-sm mb-4">Send your love and support</p>
            <a href="mailto:fanmail@themackrep.com" className="text-green-400 font-bold text-sm hover:text-green-300 transition-colors">
              fanmail@themackrep.com
            </a>
          </div>
        </div>

        {/* Download Press Kit */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-10"
        >
          <button
            onClick={() => generatePressKit()}
            className="group relative w-full flex items-center justify-center gap-3 px-8 py-5 rounded-2xl font-bold text-sm tracking-wider uppercase overflow-hidden bg-gradient-to-r from-cyan-500/15 to-purple-500/15 border border-cyan-500/30 hover:border-cyan-400/50 hover:from-cyan-500/25 hover:to-purple-500/25 transition-all duration-300 shadow-lg shadow-cyan-500/10 hover:shadow-cyan-500/20"
          >
            <svg className="w-5 h-5 text-cyan-400 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-cyan-400 group-hover:text-cyan-300 transition-colors">Download Press Kit</span>
            <span className="text-gray-500 text-xs">(PDF)</span>
          </button>
          <p className="text-center text-gray-600 text-xs mt-3">Official press materials for media & press inquiries</p>
        </motion.div>

        {/* Social Media */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm mb-6 uppercase tracking-wider font-bold">Follow The Mack Rep</p>
          <div className="flex justify-center gap-4">
            <a href="https://www.facebook.com/profile.php?id=61566553197897&mibextid=ZbWKwL" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-blue-500/50 hover:bg-blue-500/10 transition-all">
              <svg className="w-5 h-5 text-gray-400 hover:text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/themackrep?igsi=aDcwOGQwcDYwd3Jl" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-pink-500/50 hover:bg-pink-500/10 transition-all">
              <svg className="w-5 h-5 text-gray-400 hover:text-pink-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
            <a href="https://www.tiktok.com/@go.flicks?_r=1&_t=ZT-99THqzu6d5C" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-cyan-400/50 hover:bg-cyan-400/10 transition-all">
              <svg className="w-5 h-5 text-gray-400 hover:text-cyan-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46v-7.13a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-.81-.07 4.8 4.8 0 01.63-4.42z"/>
              </svg>
            </a>
            <a href="https://youtube.com/@goflicks4991?si=OdIupJvparZQNHu2" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:border-red-500/50 hover:bg-red-500/10 transition-all">
              <svg className="w-5 h-5 text-gray-400 hover:text-red-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 text-center pt-8 border-t border-white/10">
          <p className="text-3xl mb-2">👑</p>
          <p className="text-gray-500 text-xs tracking-[0.3em] uppercase">© 2024 Kiley Kelly — The Mack Rep. All Rights Reserved.</p>
        </div>
      </div>
    </motion.div>
  );
};

export default EPKPage;
